import { Fragment, useState } from 'react';
import { Alert, Box, Drawer, Toolbar } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { useApplyBranding } from '@/hooks/usePublicBranding';
import { useNavLayout } from '@/hooks/useNavLayout';
import { useSidebarCollapsed } from '@/hooks/useSidebarCollapsed';
import { useBranchContext } from '@/stores/BranchStore';
import Sidebar, { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './Sidebar';
import { PageTitleProvider } from './PageTitleContext';
import Topbar from './Topbar';
import IdleSessionGuard from '@/components/common/IdleSessionGuard';
import SetupTipBanner from './SetupTipBanner';
import SupportSessionBanner from './SupportSessionBanner';
import AssistantFab from '@/components/assistant/AssistantFab';
import BottomNav, { BOTTOM_NAV_HEIGHT } from './BottomNav';

export default function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { layout } = useNavLayout();
  const { collapsed } = useSidebarCollapsed();
  const { hasNoActiveBranch, currentBranchId } = useBranchContext();
  const isSidebar = layout === 'sidebar';
  const railWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
  useApplyBranding(); // tenant title + favicon

  return (
    <PageTitleProvider>
    <Box sx={{ display: 'flex', minHeight: '100vh', maxWidth: '100vw', overflowX: 'clip' }}>
      <Topbar onMenuClick={() => setMobileOpen((v) => !v)} />

      {/* Inside the authenticated shell, so it exists only while there is a session to end. */}
      <IdleSessionGuard />
      <AssistantFab />
      <BottomNav />

      {/* Navigation: permanent left sidebar (desktop, sidebar layout only); the mobile drawer serves both layouts. */}
      <Box
        component="nav"
        sx={{ width: { md: isSidebar ? railWidth : 0 }, flexShrink: { md: 0 }, transition: 'width .25s ease' }}
        aria-label="navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, maxWidth: '85vw', border: 0 },
          }}
        >
          <Sidebar onNavigate={() => setMobileOpen(false)} />
        </Drawer>
        {isSidebar && (
          <Drawer
            variant="permanent"
            open
            sx={{
              display: { xs: 'none', md: 'block' },
              // Full height, beside the header rather than under it. The rail now carries the
              // company mark at its top, so it owns the top-left corner and the header begins to its
              // right -- the shape the design asks for, and the reason the header is inset.
              '& .MuiDrawer-paper': {
                width: railWidth,
                border: 0,
                // The white rail is set off from the page by a hairline, as designed.
                borderRight: '1px solid',
                borderColor: 'divider',
                top: 0,
                height: '100%',
                transition: 'width .25s ease',
                overflowX: 'hidden',
              },
            }}
          >
            <Sidebar collapsed={collapsed} />
          </Drawer>
        )}
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // minWidth:0 lets this flex child shrink below its content's intrinsic width, so wide
          // children (data tables, the kanban row) scroll WITHIN their own container instead of
          // forcing page-level horizontal scroll. overflowX:hidden is a belt-and-suspenders guard.
          minWidth: 0,
          width: { md: isSidebar ? `calc(100% - ${railWidth}px)` : '100%' },
          maxWidth: '100%',
          minHeight: '100vh',
          overflowX: 'hidden',
          transition: 'width .25s ease',
          bgcolor: 'background.default',
          // Below md the bottom bar (FR-NAV-09) covers the foot of the screen; keep the last row of every page above it.
          pb: { xs: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom))`, md: 0 },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
        <Box sx={{ px: { xs: 1.5, sm: 2, md: 2.5 }, py: { xs: 1.5, sm: 2, md: 2.5 }, maxWidth: 1600, mx: 'auto', minWidth: 0 }}>
          {/* Zero-branch guard: a clear, non-blocking notice so branch-dependent screens don't read
              as "empty by mistake". Left non-blocking so an administrator can still reach the
              Branches admin page to activate a branch. */}
          {hasNoActiveBranch && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No active branch is available for your account. Please contact your administrator.
            </Alert>
          )}
          {/* Re-mount routed ERP pages whenever the current branch changes, so no component state or
              in-memory data from the previous branch can persist. Paired with the query-cache removal
              in BranchStore, this makes the server-resolved current branch part of every page's
              lifecycle without threading a branchId through each module's query keys. */}
          <SupportSessionBanner />
          <SetupTipBanner />
          <Fragment key={currentBranchId ?? 'no-branch'}>
            <Outlet />
          </Fragment>
        </Box>
      </Box>
    </Box>
    </PageTitleProvider>
  );
}
